<template>
    <div>
        <Loading :loading="loading" />
        <ModalEdit
            :formSchema="formSchema"
            :formInputs="formInputs"
            :editingItem="editingItem"
            :onSubmit="onSubmit"
            :alertMessage="alertMessage"
            :confirmarDescartar="confirmDiscard"
        >
          <template v-slot:edit-buttons="{model}">
            <slot name="edit-buttons" :model="model"></slot>
          </template>
        </ModalEdit>
        <ModalShow
            :editingItem="editingItem"
        />

        <!-- Modal Importar -->
        <modal
          v-if="enableImport"
          name="crud-importar"
          height="auto"
          width="600"
          :scrollable="true"
          :clickToClose="false"
        >
          <CloseModal :close="closeImportModal" />
          <div class="modalbox">
            <div class="modalbox-content">
              <h5>{{ importTitle || 'Importar Registros em Massa' }}</h5>

              <slot name="import-info">
                <div class="import-info-alert">
                  <CIcon name="cil-info" class="import-info-icon" />
                  <div>
                    Envie um arquivo <strong>.csv</strong> com colunas separadas por <strong>;</strong> (ponto e vírgula).
                    <br />
                    A primeira linha deve conter os nomes das colunas.
                  </div>
                </div>
              </slot>

              <div class="import-upload-area" @click="$refs.importFileInput.click()">
                <input
                  ref="importFileInput"
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  style="display: none"
                  @change="onImportFileSelected"
                />
                <template v-if="!importFile">
                  <fa-icon icon="cloud-upload-alt" class="import-upload-icon" />
                  <div class="import-upload-text">Clique para selecionar um arquivo</div>
                  <div class="import-upload-hint">.csv, .xls ou .xlsx</div>
                </template>
                <template v-else>
                  <fa-icon icon="file-alt" class="import-upload-icon selected" />
                  <div class="import-upload-text">{{ importFile.name }}</div>
                  <div class="import-upload-hint">Clique para trocar o arquivo</div>
                </template>
              </div>
            </div>
          </div>

          <div class="import-modal-footer">
            <CButton variant="outline" color="secondary" @click="closeImportModal">
              Cancelar
            </CButton>
            <CButton
              color="primary"
              @click="submitImport"
              :disabled="!importFile || importing"
            >
              {{ importing ? 'Importando...' : 'Importar' }}
            </CButton>
          </div>
        </modal>

        <div class="row">
            <div class="col-12" v-if="alerts">
              <div class="alert alert-info" style="margin: 0;">
                <strong>ATENÇÃO:</strong> {{ alerts }}
              </div>
            </div>
            <div v-if="enableSearch" class="search-filters-row" v-bind:class="{'col-6': enableSearch}">
                <span class="filters__search">
                    <input
                      type="text"
                      v-model="termo"
                      placeholder="Buscar por ..."
                      v-on:input="debounceLoadData()"
                    />
                    <fa-icon icon="search" />
                </span>
                <slot name="filter-search" />
            </div>
            <div class="text-right header-buttons" v-bind:class="{'col-6': enableSearch, 'col-12': !enableSearch}">
                <template v-if="enableImport">
                  <CButton
                    v-if="importTemplate"
                    variant="outline"
                    color="secondary"
                    size="sm"
                    @click="downloadTemplate"
                  >
                    <fa-icon icon="download" class="mr-1" />
                    Template
                  </CButton>
                  <CButton
                    variant="outline"
                    color="primary"
                    size="sm"
                    @click="openImportModal"
                  >
                    <fa-icon icon="file-upload" class="mr-1" />
                    Importar
                  </CButton>
                </template>
                <CButton color="primary" @click="onWillStore" size="sm" v-if="onStore != null">
                    Novo registro
                </CButton>
                <slot name="header-buttons" />
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <table-paginated
                    :itens="itens"
                    :columns="columns"
                    :paginator="paginator"
                    :onPageChange="loadData"
                    :onDelete="onDelete != null ? onWillDelete : null"
                    :canDelete="canDelete"
                    :onUpdate="onUpdate != null ? onWillUpdate : null"
                    :canUpdate="canUpdate"
                    :onShow="onShow != null ? onWillShow : null"
                    :onClone="onClone != null ? onWillClone : null"
                    :isLoadingData="loadingData"
                    :showActions="showActions"
                >

                    <template v-for="column in columns" v-slot:[column.field]="{model}">
                      <slot :name="column.field" :model="model"></slot>
                    </template>

                    <template v-slot:action-buttons="{model}">
                        <slot name="action-buttons" :model="model"></slot>
                    </template>

                    <template v-slot:action-buttons-after="{model}">
                        <slot name="action-buttons-after" :model="model"></slot>
                    </template>
                </table-paginated>
            </div>
        </div>
    </div>
</template>

<script>
  import clone from 'just-clone';
  import { get, post } from '@/helpers/apiRequest';
  import TablePaginated from './TablePaginated'
  import { confirmDelete, confirmUpdate, confirmStore } from "@/helpers/crud";
  import ModalEdit from "./ModalEdit";
  import ModalShow from "./ModalShow";
  import CloseModal from "@/components/CloseModal";
  import _debounce from "lodash/debounce";
  import _reduce from "lodash/reduce";

  export default {
    name: "CrudBase",
    components: {
      ModalEdit,
      ModalShow,
      TablePaginated,
      CloseModal,
    },
    props: [
      'url',
      'columns',
      'onDelete',
      'canDelete',
      'onUpdate',
      'canUpdate',
      'onStore',
      'onShow',
      'onClone',
      'formInputs',
      'enableSearch',
      'primaryKey',
      'alertMessage',
      'checkAlerts',
      'transform',
      'transformStore',
      'transformUpdate',
      'beforeSave',
      'confirmDiscard',
      'showActions',
      'reload',
      'useQueryString',
      'filters',
      'enableImport',
      'importTitle',
      'importTemplate',
    ],
    data() {
      return {
        editingItem: {},
        itens: [],
        alerts: '',
        termo: '',
        paginator: {},
        loading: false,
        loadingData: false,
        currentAction: 'store',
        formSchema: null,
        importFile: null,
        importing: false,
        currentPage: 1,
        currentTake: 15,
      }
    },
    computed: {
      filtersQuery() {
        return _reduce(this.filters, (acc, value, key) => {
          if (value) {
            acc += `&${key}=${value}`;
          }
          return acc;
        }, "");
      },
    },
    methods: {
      debounceLoadData: _debounce(function() {
        this.loadData()
      }, 300),
      loadData(page = 1, take = 15) {
        this.currentPage = page;
        this.currentTake = take;
        this.loadingData = true;

        get(`${this.url}?q=${this.termo}&page=${page}&take=${take}${this.filtersQuery}`)
          .then(json => {
            const { itens, paginator } = json;

            let transformedItems = itens;
            if (this.transform) {
              transformedItems = itens.map(item => this.transform({ ...item }));
            }

            this.itens = transformedItems;
            this.paginator = paginator;
            this.loadingData = false;
          })
          .catch((error) => {
            this.loadingData = false;
            this.$swal({
              title: 'Erro ao buscar registros',
              text: error.message || 'Não foi possível carregar a listagem.',
              icon: 'error',
              showConfirmButton: true,
            });
          });
      },
      loadAlerts() {
        if (this.checkAlerts) {
          get(`${this.url}/check-alertas`)
              .then(json => this.alerts = json.message)
        }
      },
      onWillDelete(row) {
        if (this.onDelete) {
          return confirmDelete(
              `${this.url}/${encodeURIComponent(row[this.primaryKey || 'id'])}`,
              this.loadData
          )
        }
      },
      async onWillUpdate(row) {
        if (this.onUpdate) {
          this.editingItem = clone(row);
          this.currentAction = 'update';
          await this.loadFormSchema();
          return this.$modal.show('crud-edit');
        }
      },
      async onWillStore() {
        if (this.onStore) {
          this.editingItem = {};
          this.currentAction = 'store';
          await this.loadFormSchema();
          return this.$modal.show('crud-edit');
        }
      },
      onWillShow(row) {
        if (this.onShow) {
          this.editingItem = row;
          return this.$modal.show('crud-show');
        }
      },
      async onWillClone(row) {
        if (this.onClone) {
          this.editingItem = clone(row);
          delete this.editingItem[this.primaryKey];
          this.onClone(this.editingItem);
          this.currentAction = 'store';
          await this.loadFormSchema();
          return this.$modal.show('crud-edit');
        }
      },
      async onSubmit(row) {
        /// On Success submit
        const onSuccess = () => {
          this.loadData();
          this.$modal.hide('crud-edit');
        }

        // Hook opcional executado antes de salvar (ex.: exigir motivo / confirmar produção).
        // Pode abortar (retornando false) ou enriquecer o payload (retornando um objeto).
        // O chamador (ModalEdit.submit) não faz await/catch, então uma rejeição aqui
        // viraria unhandled rejection e o "Salvar" pareceria não fazer nada.
        const runBeforeSave = async (action) => {
          if (!this.beforeSave) {
            return true;
          }
          let extra;
          try {
            extra = await this.beforeSave(row, action);
          } catch (error) {
            this.$swal({
              title: 'Falha ao validar alterações',
              text: (error && error.message) || 'Não foi possível concluir a validação antes de salvar.',
              icon: 'error',
              showCancelButton: true,
              cancelButtonText: 'Fechar',
              showConfirmButton: false,
            });
            return false;
          }
          if (extra === false) {
            return false;
          }
          if (extra && typeof extra === 'object') {
            row = { ...row, ...extra };
          }
          return true;
        };

        /// perform an update
        if (this.currentAction === 'update') {
          if (this.transformUpdate) {
            row = this.transformUpdate(row);
          }

          if (!(await runBeforeSave('update'))) {
            return;
          }

          return confirmUpdate(`${this.url}/${row[this.primaryKey || 'id']}`, row, onSuccess);
        }

        /// perform an insert
        if (this.transformStore) {
          row = this.transformStore(row);
        }

        if (!(await runBeforeSave('store'))) {
          return;
        }

        return confirmStore(`${this.url}`, row, onSuccess);
      },
      async loadFormSchema() {
        if (!!this.formSchema) {
          return;
        }

        if (!!this.formInputs) {
          this.formSchema = {
            type: "object",
            properties: this.formInputs,
            value: this.editingItem || {},
          }
          return;
        }

        this.loading = true;
        await get(`${this.url}/create`).then( response => {
          this.formSchema = {
            type: "object",
            properties: response,
            value: {},
          }
          this.loading = false;
        });
      },
      // Import methods
      openImportModal() {
        this.importFile = null;
        if (this.$refs.importFileInput) {
          this.$refs.importFileInput.value = '';
        }
        this.$modal.show('crud-importar');
      },
      closeImportModal() {
        this.$modal.hide('crud-importar');
      },
      onImportFileSelected(event) {
        this.importFile = event.target.files[0] || null;
      },
      submitImport() {
        if (!this.importFile) return;

        this.importing = true;
        const formData = new FormData();
        formData.append('arquivo', this.importFile);

        post(`${this.url}/importar`, formData, { isFile: true })
          .then((json) => {
            this.importing = false;
            this.closeImportModal();
            this.loadData();
            this.$swal({
              title: 'Importação concluída',
              text: json.message || 'Registros importados com sucesso.',
              icon: 'success',
              showConfirmButton: true,
            });
          })
          .catch((error) => {
            this.importing = false;
            this.$swal({
              title: 'Falha na Importação',
              text: error.message || 'Erro ao importar arquivo.',
              icon: 'error',
              showCancelButton: true,
              cancelButtonText: 'Fechar',
              showConfirmButton: false,
            });
          });
      },
      downloadTemplate() {
        if (!this.importTemplate) return;

        const { header, rows, filename } = this.importTemplate;
        const csv = [header, ...(rows || [])].join('\n');
        const bom = '\uFEFF';
        const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename || 'template_importacao.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      },
    },
    watch: {
      filters: {
        handler(newValue) {
          this.loadData();
        },
        deep: true,
      },
      "termo": function(newValue) {
        if (this.useQueryString || this.useQueryString === undefined) {
          history.pushState({}, null, this.$route.path + '?q=' + (newValue || ''));
        }
      },
      "reload": function (newValue, oldValue) {
        if (newValue) {
          this.loadData(this.currentPage, this.currentTake);
          this.$emit('reload', false);
        }
      },
      "itens": function (newValue) {
        this.$emit('itens', newValue)
      }
    },
    beforeMount() {
      if (this.useQueryString || this.useQueryString === undefined) {
        this.termo = this.$route.query.q || '';
      }
      this.loadData();
      this.loadAlerts();
    },
  }
</script>

<style scoped>
.search-filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-filters-row .filters__search {
  display: flex;
  align-items: center;
  height: 31px;
  box-sizing: border-box;
}

.search-filters-row .filters__search input {
  height: 100%;
  width: auto;
  flex: 1;
  padding: 4px 8px;
}

.search-filters-row ::v-deep .form-control {
  height: 31px;
  padding: 4px 8px;
  font-size: 0.875rem;
}

.header-buttons button {
  margin-left: 5px;
}

.import-modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.import-info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.import-info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

.import-upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  border: 2px dashed #dddce9;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.import-upload-area:hover {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.import-upload-icon {
  font-size: 32px;
  color: #667085;
  margin-bottom: 8px;
}

.import-upload-icon.selected {
  color: #3a3e81;
}

.import-upload-text {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.import-upload-hint {
  font-size: 12px;
  color: #667085;
  margin-top: 4px;
}

.modalbox-content h5 {
  margin-bottom: 16px;
  font-weight: 600;
}
</style>